import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash, Edit2, Eye, Layout } from "lucide-react";
import { SessionTemplate, SessionTemplateCreate, TemplateFieldType } from "../../domain/SessionTemplate";
import { createSessionTemplate, deleteSessionTemplate, listSessionTemplates, updateSessionTemplate } from "../../application/sessionTemplates";
import Button from "../../../../shared/ui/button/Button";
import Input from "../../../../shared/ui/form/input/InputField";
import Label from "../../../../shared/ui/form/Label";
import { useSubscription } from "../../../subscription";
import { Modal } from "../../../../shared/ui/modal";
import ComponentCard from "../../../../shared/ui/common/ComponentCard";
import { getTemplateName, getTemplateDescription, getFieldName } from "../utils/templateLocalization";

export default function TemplateManager() {
    const { t } = useTranslation();
    const { isPremium } = useSubscription();
    const [templates, setTemplates] = useState<SessionTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingTemplate, setEditingTemplate] = useState<SessionTemplate | null>(null);
    const [viewingTemplate, setViewingTemplate] = useState<SessionTemplate | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [filter, setFilter] = useState<'all' | 'system' | 'custom'>('all');
    const [formData, setFormData] = useState<SessionTemplateCreate>({
        name: "",
        description: "",
        fields: [],
    });

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const data = await listSessionTemplates();
            setTemplates(data);
        } catch (err) {
            console.error("Failed to load templates", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm(t("templates.confirmDelete", "Are you sure you want to delete this template?"))) return;
        try {
            await deleteSessionTemplate(id);
            fetchTemplates();
        } catch (err) {
            console.error("Failed to delete", err);
        }
    };

    const handleSave = async () => {
        try {
            if (editingTemplate) {
                await updateSessionTemplate(editingTemplate.id, formData);
            } else {
                await createSessionTemplate(formData);
            }
            setIsCreating(false);
            setEditingTemplate(null);
            fetchTemplates();
        } catch (err) {
            console.error("Failed to save template", err);
        }
    };

    const addField = () => {
        setFormData((prev) => ({
            ...prev,
            fields: [
                ...prev.fields,
                { name: "", description: "", field_type: TemplateFieldType.TEXT, order: prev.fields.length },
            ],
        }));
    };

    const removeField = (index: number) => {
        setFormData((prev) => ({
            ...prev,
            fields: prev.fields.filter((_, i) => i !== index),
        }));
    };

    return (
        <ComponentCard
            glass
            title={
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-brand-500/10 text-brand-500 flex items-center justify-center shrink-0">
                            <Layout className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                            <span className="block text-lg font-bold text-content-primary truncate">
                                {t("settings.tabs.templates")}
                            </span>
                            <p className="hidden sm:block text-xs text-content-tertiary font-normal">
                                {t("settings.templatesDescription")}
                            </p>
                        </div>
                    </div>
                    {isPremium && !isCreating && !editingTemplate && (
                        <Button
                            onClick={() => {
                                setIsCreating(true);
                                setEditingTemplate(null);
                                setFormData({ name: "", description: "", fields: [] });
                            }}
                            className="shadow-lg shadow-brand-500/20 w-full sm:w-auto"
                        >
                            {t("templates.createCustom", "Create custom")}
                        </Button>
                    )}
                </div>
            }
        >
            {!isCreating && !editingTemplate && (
                <>
                    <p className="sm:hidden text-sm text-content-tertiary mb-6 px-1">
                        {t("settings.templatesDescription")}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-8">
                        <button
                            onClick={() => setFilter('all')}
                            className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${filter === 'all'
                                ? "bg-brand-600 text-white"
                                : "bg-gray-100 dark:bg-white/5 text-content-secondary hover:bg-gray-200 dark:hover:bg-white/10"
                                }`}
                        >
                            {t("templates.filters.all", "All")}
                        </button>
                        <button
                            onClick={() => setFilter('system')}
                            className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${filter === 'system'
                                ? "bg-brand-600 text-white"
                                : "bg-gray-100 dark:bg-white/5 text-content-secondary hover:bg-gray-200 dark:hover:bg-white/10"
                                }`}
                        >
                            {t("templates.filters.alchely", "Alchely templates")}
                        </button>
                        {isPremium && (
                            <button
                                onClick={() => setFilter('custom')}
                                className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${filter === 'custom'
                                    ? "bg-purple-600 text-white"
                                    : "bg-gray-100 dark:bg-white/5 text-content-secondary hover:bg-gray-200 dark:hover:bg-white/10"
                                    }`}
                            >
                                {t("templates.filters.custom", "Custom")}
                            </button>
                        )}
                    </div>
                </>
            )}

            {!isCreating && !editingTemplate && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {loading ? (
                        <div className="col-span-full py-12 text-center text-gray-500">{t("common.loading", "Loading...")}</div>
                    ) : templates.length === 0 ? (
                        <div className="col-span-full py-12 text-center text-gray-500">{t("templates.noTemplates", "No templates found.")}</div>
                    ) : (
                        templates
                            .filter((tpl) => {
                                if (filter === 'system') return !tpl.is_custom;
                                if (filter === 'custom') return tpl.is_custom;
                                return true;
                            })
                            .map((tpl) => (
                                <div key={tpl.id} className="flex flex-col h-full border border-gray-100 dark:border-white/5 bg-white/50 dark:bg-white/[0.02] rounded-2xl shadow-theme-xs hover:shadow-theme-md transition-all p-5 sm:p-6">
                                    <div className="flex justify-between items-start mb-3 sm:mb-4">
                                        <div className="min-w-0">
                                            <h3 className="text-base sm:text-lg font-bold text-content-primary truncate">{getTemplateName(tpl, t)}</h3>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {!tpl.is_custom && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider bg-accent-tint text-accent-text bg-accent-tint text-accent-text border border-accent/20">
                                                        {t("templates.systemDefault", "Default")}
                                                    </span>
                                                )}
                                                {tpl.is_custom && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider bg-purple-50 text-purple-700 dark:bg-purple-500/10 dark:text-purple-400 border border-purple-100 dark:border-purple-500/20">
                                                        {t("templates.customLabel", "Custom")}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex space-x-1 sm:space-x-2 shrink-0">
                                            <button onClick={() => setViewingTemplate(tpl)} className="p-2 text-content-tertiary hover:text-brand-600 transition-colors" title={t("templates.viewExample", "View")}>
                                                <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                                            </button>
                                            {tpl.is_custom && (
                                                <>
                                                    <button onClick={() => {
                                                        setEditingTemplate(tpl);
                                                        setFormData({ name: tpl.name, description: tpl.description, fields: tpl.fields });
                                                    }} className="p-2 text-content-tertiary hover:text-accent-text transition-colors">
                                                        <Edit2 className="w-4 h-4 sm:w-5 sm:h-5" />
                                                    </button>
                                                    <button onClick={() => handleDelete(tpl.id)} className="p-2 text-content-tertiary hover:text-red-600 transition-colors">
                                                        <Trash className="w-4 h-4 sm:w-5 sm:h-5" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-xs sm:text-sm text-content-tertiary flex-grow mb-4 line-clamp-2">{getTemplateDescription(tpl, t)}</p>
                                    <div className="flex items-center text-xs sm:text-xs font-bold text-content-tertiary uppercase tracking-wider">
                                        <span className="mr-1">{tpl.fields.length}</span> {t("templates.fields", "fields")}
                                    </div>
                                </div>
                            ))
                    )}
                </div>
            )}

            <Modal
                isOpen={isCreating || !!editingTemplate}
                onClose={() => {
                    setIsCreating(false);
                    setEditingTemplate(null);
                }}
                className="max-w-2xl"
            >
                <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">
                        {editingTemplate ? t("templates.editTemplate", "Edit Template") : t("templates.createCustom", "Create custom template")}
                    </h3>

                    <div className="space-y-6">
                        <div>
                            <Label className="mb-2 block text-sm font-medium">{t("common.name", "Name")}</Label>
                            <Input
                                value={formData.name}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            />
                        </div>
                        <div>
                            <Label className="mb-2 block text-sm font-medium">{t("common.description", "Description")}</Label>
                            <Input
                                value={formData.description || ""}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            />
                        </div>

                        <div className="my-6">
                            <div className="flex justify-between items-center mb-4">
                                <h4 className="font-medium text-gray-900">{t("templates.fields", "Fields")}</h4>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={addField}
                                    startIcon={<Plus className="w-3 h-3" />}
                                >
                                    {t("templates.addField", "Add Field")}
                                </Button>
                            </div>

                            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                                {formData.fields.map((field, idx) => (
                                    <div key={idx} className="flex gap-4 items-start p-4 border border-gray-100 rounded-lg bg-gray-50/50">
                                        <div className="flex-1 space-y-4">
                                            <div>
                                                <Label className="mb-2 block text-sm font-medium">{t("templates.fieldName", "Field Name")}</Label>
                                                <Input
                                                    value={field.name}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        const newFields = [...formData.fields];
                                                        newFields[idx].name = e.target.value;
                                                        setFormData({ ...formData, fields: newFields });
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <Label className="mb-2 block text-sm font-medium">{t("templates.fieldDescription", "Description")}</Label>
                                                <Input
                                                    value={field.description}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        const newFields = [...formData.fields];
                                                        newFields[idx].description = e.target.value;
                                                        setFormData({ ...formData, fields: newFields });
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <button onClick={() => removeField(idx)} className="p-2 text-content-tertiary hover:text-red-600 transition-colors mt-6">
                                            <Trash className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                                {formData.fields.length === 0 && (
                                    <p className="text-sm text-center text-content-tertiary py-4">{t("templates.noFields", "No fields added yet.")}</p>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 dark:border-gray-800">
                            <Button variant="outline" onClick={() => {
                                setIsCreating(false);
                                setEditingTemplate(null);
                            }}>
                                {t("common.cancel", "Cancel")}
                            </Button>
                            <Button onClick={handleSave} disabled={!formData.name || formData.fields.length === 0}>
                                {t("common.save", "Save")}
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={!!viewingTemplate}
                onClose={() => setViewingTemplate(null)}
                className="max-w-2xl"
            >
                <div className="p-6">
                    <div className="flex justify-between items-start mb-6">
                        <div>
                            <h3 className="text-xl font-bold text-gray-900">{viewingTemplate ? getTemplateName(viewingTemplate, t) : ""}</h3>
                            <p className="text-sm text-gray-500 mt-1">{viewingTemplate ? getTemplateDescription(viewingTemplate, t) : ""}</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h4 className="font-semibold text-gray-800 border-b pb-2">{t("templates.fieldsStructure", "Fields Structure")}</h4>
                        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                            {viewingTemplate?.fields.map((field, idx) => (
                                <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                                    <div className="font-medium text-gray-800">{getFieldName(field.name, t)}</div>
                                    <div className="text-xs text-gray-500 mt-1">{field.description}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-8 flex justify-end">
                        <Button variant="outline" onClick={() => setViewingTemplate(null)}>
                            {t("common.close", "Close")}
                        </Button>
                    </div>
                </div>
            </Modal>
        </ComponentCard>
    );
}
